import type { Application, WorkspaceUser } from '@/api/types'
import { memberLabel } from '@/features/workspace/members'
import { dayHeading } from '@/shared/lib/when'

/**
 * The workspace's databases and dashboards as one list: the tabs that slice it,
 * the filters that narrow it, and the owner groups the "By Owner" tab folds it
 * into. Pure, so the page only renders what this decides.
 */

export const LISTING_TABS = ['mine', 'name', 'owner'] as const
export type ListingTab = (typeof LISTING_TABS)[number]

export const ACCESS_TAGS = ['everyone', 'restricted', 'closed', 'noAi'] as const
export type AccessTag = (typeof ACCESS_TAGS)[number]

/** Disjoint, so choosing several reads as "any of these". */
export const MODIFIED_BUCKETS = ['today', 'week', 'month', 'older'] as const
export type ModifiedBucket = (typeof MODIFIED_BUCKETS)[number]

/** Every filter is a set of values; an empty set does not filter. */
export interface ListingFilters {
  search: string
  type: readonly string[]
  owner: readonly string[]
  access: readonly string[]
  modified: readonly string[]
}

/** The key an application with no known owner is grouped and filtered under. */
export const UNKNOWN_OWNER = 'none'

export type OwnerApp = NonNullable<Application['owner_app']>

/**
 * A person, a Lithos Apps app with the member who answers for it, or nobody known:
 * an application made before an owner was recorded, or by somebody who has left.
 */
export type Owner =
  | { kind: 'person'; key: string; member: WorkspaceUser }
  | { kind: 'app'; key: string; app: OwnerApp; manager: WorkspaceUser | undefined }
  | { kind: 'unknown'; key: typeof UNKNOWN_OWNER }

export function ownerOf(
  application: Application,
  members: ReadonlyMap<number, WorkspaceUser>,
): Owner {
  const app = application.owner_app
  if (app !== null) {
    return {
      kind: 'app',
      key: `app:${String(app.id)}`,
      app,
      manager: app.owner_user === null ? undefined : members.get(app.owner_user),
    }
  }
  const member =
    application.created_by === null ? undefined : members.get(application.created_by)
  return member === undefined
    ? { kind: 'unknown', key: UNKNOWN_OWNER }
    : { kind: 'person', key: `user:${String(member.user_id)}`, member }
}

/** Owned by this account, or by an app it answers for. */
export function isMine(application: Application, userId: number | undefined): boolean {
  if (userId === undefined) return false
  return application.owner_app === null
    ? application.created_by === userId
    : application.owner_app.owner_user === userId
}

export function membersById(
  members: readonly WorkspaceUser[],
): Map<number, WorkspaceUser> {
  return new Map(members.map((member) => [member.user_id, member]))
}

/** Who may open it, plus whether AI connections are shut out. */
export function accessTags(application: Application): AccessTag[] {
  const tags: AccessTag[] = [
    !application.user_can_access
      ? 'closed'
      : application.restrict_access
        ? 'restricted'
        : 'everyone',
  ]
  if (!application.mcp_access) tags.push('noAi')
  return tags
}

const DAY_MS = 24 * 60 * 60 * 1000

export function modifiedBucket(iso: string, now: Date): ModifiedBucket {
  if (dayHeading(iso, now).kind === 'today') return 'today'
  const age = now.getTime() - new Date(iso).getTime()
  if (age < 7 * DAY_MS) return 'week'
  if (age < 30 * DAY_MS) return 'month'
  return 'older'
}

export function filterApplications(
  applications: readonly Application[],
  filters: ListingFilters,
  members: ReadonlyMap<number, WorkspaceUser>,
  now: Date,
): Application[] {
  const term = filters.search.trim().toLowerCase()
  const any = (chosen: readonly string[], values: readonly string[]) =>
    chosen.length === 0 || values.some((value) => chosen.includes(value))

  return applications.filter(
    (application) =>
      any(filters.type, [application.type]) &&
      any(filters.owner, [ownerOf(application, members).key]) &&
      any(filters.access, accessTags(application)) &&
      any(filters.modified, [modifiedBucket(application.updated_on, now)]) &&
      (term === '' ||
        [application.name, application.description ?? ''].some((text) =>
          text.toLowerCase().includes(term),
        )),
  )
}

const COLLATOR = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })

export function byName(a: Application, b: Application): number {
  return COLLATOR.compare(a.name, b.name) || a.id - b.id
}

/** Most recently modified first. */
export function byModified(a: Application, b: Application): number {
  return b.updated_on.localeCompare(a.updated_on) || byName(a, b)
}

export type OwnerGroup = Owner & {
  applications: Application[]
  /** The latest `updated_on` among them. */
  updatedOn: string
}

/**
 * One group per owner, most recently active first and the unknown owner last;
 * each group's applications by name.
 */
export function groupByOwner(
  applications: readonly Application[],
  members: ReadonlyMap<number, WorkspaceUser>,
): OwnerGroup[] {
  const groups = new Map<string, OwnerGroup>()
  for (const application of applications) {
    const owner = ownerOf(application, members)
    const group = groups.get(owner.key)
    if (group === undefined) {
      groups.set(owner.key, {
        ...owner,
        applications: [application],
        updatedOn: application.updated_on,
      })
    } else {
      group.applications.push(application)
      if (application.updated_on > group.updatedOn) {
        group.updatedOn = application.updated_on
      }
    }
  }
  return [...groups.values()]
    .map((group) => ({ ...group, applications: group.applications.sort(byName) }))
    .sort(
      (a, b) =>
        Number(a.key === UNKNOWN_OWNER) - Number(b.key === UNKNOWN_OWNER) ||
        b.updatedOn.localeCompare(a.updatedOn) ||
        ownerLabelCompare(a, b),
    )
}

/** What to call an owner; `undefined` for nobody known. */
export function ownerName(owner: Owner): string | undefined {
  if (owner.kind === 'app') return owner.app.display_name
  if (owner.kind === 'person') return memberLabel(owner.member)
  return undefined
}

function ownerLabelCompare(a: Owner, b: Owner): number {
  return COLLATOR.compare(ownerName(a) ?? '', ownerName(b) ?? '')
}
